import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useConfirm } from '../components/ConfirmPopup';
import { removeToast, showToast } from '../context/ToastHandle';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { getBoardLimit, getDecodedToken, getBoardUsage } from '../context/accountUtils'; // ⬅️ added getDecodedToken
import ShareBoardButton from '../components/ShareBoardButton';
import Popup from '../components/Popup';
import { clearBoardCaches } from '../context/cacheUtils';
import StickyCoreSettings, { loadStickyCoreCache, saveStickyCoreCache } from '../components/StickyCoreSettings';

// ⬇️ react-dnd
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

const DND_TYPE = 'BOARD_ROW';

const cacheBoardConfig = async (cache, board) => {
  const configCacheKey = `/aac/api/getconfig.php?id=${board.id}`;
  const configPayload = {
    id: board.id,
    name: board.name || '',
    grid_config:
      board.grid_config || JSON.stringify({ rows: 5, columns: 8 }),
    scrollable: board.scrollable ?? 0,
    square_buttons: board.square_buttons ?? 1,
    theme: board.theme || 'default',
  };

  const response = new Response(JSON.stringify(configPayload), {
    status: 200,
    headers: {
      'Content-Type': 'application/json; charset=UTF-8',
      Date: new Date().toUTCString(),
    },
  });

  await cache.put(configCacheKey, response);
};

const cacheBoardTiles = async (cache, boardId, tiles) => {
  const response = new Response(JSON.stringify(Array.isArray(tiles) ? tiles : []), {
    status: 200,
    headers: {
      'Content-Type': 'application/json; charset=UTF-8',
      Date: new Date().toUTCString(),
    },
  });

  await cache.put(`/aac/api/tiles.php?board_id=${boardId}`, response);
};

const cacheJsonResponse = async (cache, key, payload) => {
  const response = new Response(JSON.stringify(payload), {
    status: 200,
    headers: {
      'Content-Type': 'application/json; charset=UTF-8',
      Date: new Date().toUTCString(),
    },
  });

  await cache.put(key, response);
};

const prefetchImages = async (urls, concurrency = 8) => {
  const uniqueUrls = [...new Set((urls || []).filter(Boolean))];
  let cursor = 0;

  const worker = async () => {
    while (cursor < uniqueUrls.length) {
      const url = uniqueUrls[cursor++];

      try {
        await fetch(url);
      } catch {
        console.debug('Image prefetch failed:', url);
      }
    }
  };

  const workerCount = Math.min(concurrency, uniqueUrls.length);
  await Promise.allSettled(
    Array.from({ length: workerCount }, () => worker())
  );
};



const LoadingOverlay = ({ show, text = 'Loading…' }) => {
  if (!show) return null;
  return (
    <div style={overlayStyles.container}>
      <div style={overlayStyles.card}>
        <div className="spinner" />
        <p style={{ marginTop: 8 }}>{text}</p>
      </div>
    </div>
  );
};

const BoardList = () => {
  const [boards, setBoards] = useState([]);
  const [creating, setCreating] = useState(false);
  const [newBoardName, setNewBoardName] = useState('');
  const [renamingBoardId, setRenamingBoardId] = useState(null);
  const [renamingBoardName, setRenamingBoardName] = useState('');
  const [boardCacheStatus, setBoardCacheStatus] = useState({});
  const [loading, setLoading] = useState(true); // drives overlay on the list region
  const [tutorialIsCached, setTutorialIsCached] = useState(false);
  const [showPopup, setShowPopup] = useState(false);

  // ⬇️ Tutorial additions
  const [tutorialSummary, setTutorialSummary] = useState(null);
  const userId = getDecodedToken()?.sub;
  const seenKey = `tutorialPromptSeen:${userId}`;
  const [showTutorialPrompt, setShowTutorialPrompt] = useState(() => !localStorage.getItem(seenKey));
  // ⬆️ Tutorial additions

  // ⬇️ DnD + persist
  const persistTimerRef = useRef(null);
  const isPersistingRef = useRef(false);
  // ⬆️ DnD + persist

  const navigate = useNavigate();
  const { showConfirm } = useConfirm();

  useEffect(() => {
    fetchBoards();
    fetchTutorialSummary(); // ⬅️ Tutorial: load summary in parallel
  }, []);

  const fetchBoards = async () => {
    setLoading(true);
    const toastEl = showToast('Caching boards for offline use…', 'info', true);

    try {
      const syncRes = await api.get('/offline-sync.php');
      const syncData = syncRes?.data || {};

      const boardList = Array.isArray(syncData.boards) ? syncData.boards : [];
      const tilesByBoard =
        syncData.tiles && typeof syncData.tiles === 'object'
          ? syncData.tiles
          : {};

      const normalized = boardList
        .map((b, i) => ({
          ...b,
          position: Number.isFinite(Number(b.position)) ? Number(b.position) : i,
        }))
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

      setBoards(normalized);

      const cache = await caches.open('api-cache');
      const now = Date.now();

      await Promise.all(
        normalized.map(async (board) => {
          const boardTiles = Array.isArray(tilesByBoard[String(board.id)])
            ? tilesByBoard[String(board.id)]
            : Array.isArray(tilesByBoard[board.id])
              ? tilesByBoard[board.id]
              : [];

          await Promise.all([
            cacheBoardConfig(cache, board),
            cacheBoardTiles(cache, board.id, boardTiles),
          ]);

          localStorage.setItem(`boardCacheAt:${board.id}`, now.toString());
        })
      );

      await cacheJsonResponse(cache, '/aac/api/boards.php', normalized);
      localStorage.setItem('boardsCachedAt', now.toString());

      const imageUrls = normalized.flatMap((board) => {
        const boardTiles = tilesByBoard[String(board.id)] || tilesByBoard[board.id] || [];
        return Array.isArray(boardTiles)
          ? boardTiles.map((tile) => tile?.image_url).filter(Boolean)
          : [];
      });

      await prefetchImages(imageUrls, 8);

      try {
        const TUTORIAL_STALE_MS = 1000 * 60 * 60 * 24 * 10;
        const tutorialCacheKey = '/aac/api/tutorial.php?tiles=1';
        const tutorialMatch = await cache.match(tutorialCacheKey);
        const lastTutorialPrefetch = Number(localStorage.getItem('tutorialCachedAt') || 0);
        const tutorialIsStale = now - lastTutorialPrefetch > TUTORIAL_STALE_MS;

        if (tutorialMatch && !tutorialIsStale) {
          setTutorialIsCached(true);
        } else {
          const tutRes = await api.get('/tutorial.php?tiles=1');
          const tutTiles = Array.isArray(tutRes.data) ? tutRes.data : [];

          await prefetchImages(
            tutTiles.map((tile) => tile?.image_url).filter(Boolean),
            8
          );

          setTutorialIsCached(true);
          localStorage.setItem('tutorialCachedAt', Date.now().toString());
        }
      } catch (e) {
        console.debug('Tutorial prefetch error:', e);
      }

      const results = {};
      normalized.forEach((board) => {
        results[board.id] = true;
      });
      setBoardCacheStatus(results);

      console.log(`Bulk offline sync cached ${normalized.length} boards`);
    } catch (err) {
      console.error('Failed to load boards or cache status:', err);
    } finally {
      removeToast(toastEl);
      setLoading(false);
    }
  };

  function CommunityBoards({ onOpen }) {
    return (
      <div
        className="tutorial-card tutorial-card--clickable"
        onClick={onOpen}
        role="button"
        tabIndex={0}
      >
        <div className="tutorial-card__body">
          <div className="tutorial-card__header">
            <h4 className="tutorial-card__title">Community Boards</h4>
            <span className="badge badge--warn" title="Not cached yet">Online only</span>
          </div>
          <div className="tutorial-card__meta">
            <div className="tutorial-card__desc">
              Browse &amp; import boards shared by other users.
            </div>
          </div>
        </div>
          {/* ✅ Play icon on the right */}
          <img src="/aac/api/imgs/playbutton.png" alt="" className="tutorial-card__play" />
      </div>
    );
  }

function PresetBoardsCard({ onOpen }) {
  return (
    <div className="tutorial-card tutorial-card--clickable" onClick={onOpen} role="button" tabIndex={0}>
      <div className="tutorial-card__body">
        <div className="tutorial-card__header">
          <h4 className="tutorial-card__title">Preset Boards</h4>
          <span className="badge badge--warn" title="Online library">Online library</span>
        </div>
        <div className="tutorial-card__meta"><div className="tutorial-card__desc">Search &amp; add ready-made AAC boards for everyday communication and learning.</div></div>
      </div>
                {/* ✅ Play icon on the right */}
          <img src="/aac/api/imgs/playbutton.png" alt="" className="tutorial-card__play" />
    </div>
  );
}

function TutorialCard({ isCached, onOpen }) {
  return (
    <div
      className="tutorial-card tutorial-card--clickable"
      onClick={onOpen}
      role="button"
      tabIndex={0}
    >
     <div className="tutorial-card__body">
        <div className="tutorial-card__header">
          <h4 className="tutorial-card__title">Tutorial</h4>
          {isCached ? (
            <span className="badge badge--ok" title="Available offline">Available Offline</span>
          ) : (
            <span className="badge badge--warn" title="Not cached yet">Online only</span>
          )}
        </div>

        <p className="tutorial-card__desc">
          Learn how to navigate boards, edit tiles, and use speech quickly.
        </p>
      </div>

      {/* ✅ Play icon on the right */}
      <img src="/aac/api/imgs/playbutton.png" alt="" className="tutorial-card__play" />
    </div>
  );
}

  // ⬇️ Tutorial: summary + open helpers
  const fetchTutorialSummary = async () => {
    try {
      const res = await api.get('/tutorial.php?summary=1'); // {exists, title, tileCount}
      setTutorialSummary(res.data);
    } catch (e) {
      console.warn('Tutorial summary failed:', e);
      setTutorialSummary({ exists: false, title: 'Tutorial', tileCount: 0 });
    }
  };

  const openTutorial = async (start = false) => {
    window.location.href = '/aac/tutorial/';
  };
  // ⬆️ Tutorial

  const startRenaming = (board) => {
    setRenamingBoardId(board.id);
    setRenamingBoardName(board.name || '');
  };

  const cancelRenaming = () => {
    setRenamingBoardId(null);
    setRenamingBoardName('');
  };

  const saveRenamedBoard = async (boardId) => {
    if (!renamingBoardName.trim()) return;
    try {
      await api.put(`/boards.php?id=${boardId}`, { name: renamingBoardName.trim() });
      showToast('Board renamed!', 'success', false);
      cancelRenaming();
      fetchBoards();
    } catch (err) {
      console.error('Rename failed:', err);
      showToast('Rename failed.', 'error', false);
    }
  };

  const createBoard = async () => {
    if (!newBoardName.trim()) return;

    const limit = getBoardLimit();
    if (boards.length >= limit) {
      showToast(`🚫 Board limit reached (${limit}). Upgrade your plan to add more.`, 'error', false);
      return;
    }

    setCreating(true);
    try {
      const grid_config = { columns: 8, rows: 5 };
      const res = await api.post('/boards.php', { name: newBoardName.trim(), grid_config });
      const newId = res.data.id;

      // const tilePromises = Array.from({ length: 40 }).map((_, index) =>
      //   api.post('/tiles.php', {
      //     board_id: newId,
      //     label: '',
      //     color: '#eeeeee',
      //     image_url: '',
      //     position: index
      //   })
      // );
      // await Promise.all(tilePromises);
      showToast(`${newBoardName.trim()} created successfully!`, 'success', false);
      // navigate(`/board/${newId}/edit`);
      fetchBoards();
    } catch (err) {
      showToast(`🚨 Error creating board: ${err.response?.data || err.message}`, 'error', false);
    } finally {
      setCreating(false);
      setNewBoardName('');
    }
  };

  const deleteBoard = async (id) => {
    const confirmed = await showConfirm('Are you sure you want to delete this board?');
    if (confirmed) {
      try {
        await api.delete(`/boards.php?id=${id}`);

        // 1) remove from UI immediately
        setBoards(prev => prev.filter(b => String(b.id) !== String(id)));
        setBoardCacheStatus(prev => {
          const copy = { ...prev };
          delete copy[id];
          return copy;
        });

        // 2) invalidate cached list response (SWR would otherwise re-serve stale)
        if (navigator.onLine) {
          try {
            const cache = await caches.open('api-cache');
            await cache.delete('/aac/api/boards.php');
          } catch {}
        }

        // 3) clear per-board caches
        await clearBoardCaches(id);

        // 4) refresh from server
        fetchBoards();

        window.dispatchEvent(new CustomEvent('sticky-core-changed'));
        showToast('Deleted successfully!', 'success', false);
      } catch (err) {
        showToast('Failed to delete board', 'error', false);
        console.error('Failed to delete board:', err);
      }
    }
  };

  const duplicateBoard = async (id) => {
    const limit = getBoardLimit();
    if (boards.length >= limit) {
      showToast(`🚫 Board limit reached (${limit}). Upgrade your plan to add more.`, 'error', false);
      return;
    }
    try {
      const res = await api.post(`/boards.php?duplicate=${id}`);
      const newId = res.data.id;
      navigate(`/board/${newId}/edit`);
    } catch (err) {
      console.error('Failed to duplicate board:', err);
    }
  };

  const downloadBoardTiles = async (boardId, boardName) => {
    try {
      showToast('Preparing download...', 'info', true);
      const res = await api.get(`/tiles.php?board_id=${boardId}`);
      const tiles = res.data;
      const arasaacTiles = tiles.filter(tile => tile.image_url && tile.image_url.includes('arasaac.org'));
      if (arasaacTiles.length === 0) {
        showToast('No ARASAAC tiles to download for this board.', 'info', false);
        return;
      }
      const zip = new JSZip();
      const folder = zip.folder(boardName.replace(/\s+/g, '_') || 'board_tiles');
      for (const tile of arasaacTiles) {
        const response = await fetch(tile.image_url);
        const blob = await response.blob();
        const filename = (tile.label ? tile.label.replace(/\s+/g, '_') : 'tile') + '.png';
        folder.file(filename, blob);
      }
      const content = await zip.generateAsync({ type: 'blob' });
      saveAs(content, `${boardName.replace(/\s+/g, '_')}_tiles.zip`);
      showToast('Tiles zipped and downloading...', 'info', false);
    } catch (err) {
      console.error('Failed to download tiles:', err);
      showToast('Failed to download tiles.', 'error', false);
    }
  };

  // ---- DnD: move + persist order ----
  const moveRow = (fromIndex, toIndex) => {
    setBoards(prev => {
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      // Recompute positions locally
      next.forEach((b, i) => { b.position = i; });
      // Debounced persist
      schedulePersist(next);
      return next;
    });
  };

  const schedulePersist = (orderedBoards) => {
    if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    persistTimerRef.current = setTimeout(() => persistOrder(orderedBoards), 500);
  };

  const persistOrder = async (orderedBoards) => {
    if (isPersistingRef.current) return;
    isPersistingRef.current = true;
    try {
      // PUT each board with its new position (keeps API simple & consistent with your rename)
      await Promise.all(
        orderedBoards.map((b, i) =>
          api.put(`/boards.php?id=${b.id}`, { position: i }).catch(() => null)
        )
      );
      // Optional: showToast('Order saved', 'success', false);
    } catch (e) {
      console.error('Order persist failed', e);
      showToast('Failed to save new order', 'error', false);
    } finally {
      isPersistingRef.current = false;
    }
  };
  // ---- /DnD ----

  const limit = getBoardLimit();

  return (
    <div style={{ padding: '1rem', maxWidth: '600px', margin: '0 auto' }}>
      <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>Your Boards</h2>
      <h2 style={{ fontSize: '1rem', marginBottom: '1rem' }}>
        You've used {boards.length}/{limit} boards. {boards.length >= limit ? '(Upgrade your plan to add more.)' : ''}
      </h2>

      {/* Tutorial prompt (first login) */}
      {showTutorialPrompt && (
        <div style={promptStyles.backdrop}>
          <div style={promptStyles.modal}>
            <h3 style={{ marginTop: 0 }}>Take a quick tutorial?</h3>
            <p style={{ margin: '8px 0 16px' }}>Learn the basics in under 2 minutes.</p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                onClick={() => { localStorage.setItem(seenKey, '1'); setShowTutorialPrompt(false); openTutorial(true); }}
                style={buttonStyle('#007bff')}
              >
                Yes, guide me
              </button>
              <button
                onClick={() => { localStorage.setItem(seenKey, '1'); setShowTutorialPrompt(false); }}
                style={buttonStyle('#6c757d')}
              >
                Skip
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', marginBottom: '1rem', gap: '10px', flexWrap: 'wrap' }}>
        <button
          onClick={() => setShowPopup(true)}
          style={{ padding: '0.5rem 1rem', backgroundColor: '#28a745', color: '#fff', border: 'none', borderRadius: '4px' }}
        >
          Add Board
        </button>

        <button
          onClick={() => navigate('/smart-setup')}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: '#6f42c1',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontWeight: 600
          }}
          title="Create a personalised board"
        >
          ✨ Create with AI
        </button>
      </div>

      <StickyCoreSettings />

      {/* Fixed Tutorial card above the list; not draggable, not deletable */}
      {<TutorialCard
            isCached={tutorialIsCached}
            onOpen={() => navigate('/tutorial')}
        />}

      {/* Fixed Tutorial card above the list; not draggable, not deletable */}
      {<CommunityBoards
            onOpen={() => navigate('/community')}
        />}

        {<PresetBoardsCard
            onOpen={() => navigate('/presets')}
        />}

      {/* List area with relative positioning so overlay can sit on top */}
      <div style={{ position: 'relative', minHeight: 120 }}>
        <h3 style={{ marginBottom: '1rem' }}>Boards</h3>
        <LoadingOverlay show={loading} text="Loading boards…" />

        {(!boards || boards.length === 0) && !loading ? (
          <p>No boards found.</p>
        ) : (
          <DndProvider backend={HTML5Backend}>
            <div>
              {boards.map((board, index) => (
                <BoardRow
                  key={board.id}
                  index={index}
                  board={board}
                  isOffline={!!boardCacheStatus[board.id]}
                  renamingBoardId={renamingBoardId}
                  renamingBoardName={renamingBoardName}
                  onStartRenaming={startRenaming}
                  onCancelRenaming={cancelRenaming}
                  onSaveRenamedBoard={saveRenamedBoard}
                  onUse={() => navigate(`/board/${board.id}`)}
                  onEdit={() => navigate(`/board/${board.id}/edit`)}
                  onDuplicate={() => duplicateBoard(board.id)}
                  onDelete={() => deleteBoard(board.id)}
                  moveRow={moveRow}
                />
              ))}
            </div>
          </DndProvider>
        )}

        {/* Keep the Add Board popup mounted even when the user has no boards. */}
          <Popup
            isOpen={showPopup}
            onClose={() => setShowPopup(false)}
            availableBoards={boards}
            onSubmit={async (boardData) => {
              const usage = await getBoardUsage();
                if (usage.isLimitReached) {
              showToast(
                `Board limit reached for your plan, You have used ${usage.count} of ${usage.limit} boards.`,
                'error'
              );
                  return;
                }

            try {
              const createRes = await api.post('/boards.php', boardData);

              if (boardData.is_core_board && createRes?.data?.id) {
                await api.put('/core-settings.php', {
                  action: 'set_board_core',
                  board_id: Number(createRes.data.id),
                  is_core_board: true,
                });
                window.dispatchEvent(new CustomEvent('sticky-core-changed'));
              }

              setShowPopup(false);
              showToast('Board created successfully!', 'success', false);

              if (navigator.onLine) {
                try {
                  const cache = await caches.open('api-cache');
                  await cache.delete('/aac/api/boards.php');
                } catch (cacheErr) {
                  console.debug('Failed to clear boards cache:', cacheErr);
                }
              }

              await fetchBoards();
            } catch (err) {
              console.error('Error creating board:', err);
              showToast(
                `Error creating board: ${err.response?.data?.message || err.response?.data || err.message}`,
                'error',
                false
              );
            }
          }}
        />
      </div>
    </div>
  );
};

// ---- DnD row component (drag source + drop target) ----
const BoardRow = ({
  board,
  index,
  isOffline,
  renamingBoardId,
  renamingBoardName,
  onStartRenaming,
  onCancelRenaming,
  onSaveRenamedBoard,
  onUse,
  onEdit,
  onDuplicate,
  onDelete,
  moveRow,
}) => {
  const ref = useRef(null);

  const [, drop] = useDrop({
    accept: DND_TYPE,
    hover(item) {
      if (!ref.current) return;
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) return;
      moveRow(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: DND_TYPE,
    item: { id: board.id, index },
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  drag(drop(ref));

  return (
    <div
      ref={ref}
      style={{
        border: '1px solid #ccc',
        borderRadius: '6px',
        padding: '1rem',
        marginBottom: '1rem',
        background: '#fff',
        opacity: isDragging ? 0.6 : 1,
        cursor: 'grab',
        transition: 'background 0.15s, box-shadow 0.15s, transform 0.15s',
      }}
      className='boardCard'
    >
      {renamingBoardId === board.id ? (
        <input
          type="text"
          value={renamingBoardName}
          autoFocus
          onChange={(e) => onStartRenaming({ id: board.id, name: e.target.value }) || null}
          onBlur={() => onSaveRenamedBoard(board.id)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') onSaveRenamedBoard(board.id);
            if (e.key === 'Escape') onCancelRenaming();
          }}
          style={{ fontWeight: 'bold', fontSize: '1rem', padding: '4px', border: '1px solid #ccc', borderRadius: '4px', width: '100%' }}
        />
      ) : (
        <div
          style={{ fontWeight: 'bold', cursor: 'text' }}
          onClick={() => onStartRenaming(board)}
          title="Click to rename"
        >
          {board.name || `Board #${board.id}`}
        </div>
      )}

      {isOffline ? (
            <span className="badge badge--ok" title="Available offline">Available Offline</span>
      ) : (
            <span className="badge badge--warn" title="Not cached yet">Online only</span>
      )}

      <div style={{ fontSize: '0.875rem', color: '#666', marginBottom: '0.5rem' }}>
        Created: {new Date(board.created_at).toLocaleString()}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
        <button onClick={onUse} style={buttonStyle('#007bff')}>Use</button>
        <button onClick={onEdit} style={buttonStyle('#6c757d')}>Edit</button>
        <button onClick={onDuplicate} style={buttonStyle('#17a2b8')}>Duplicate</button>
        <button onClick={onDelete} style={buttonStyle('#dc3545')}>Delete</button>
        <ShareBoardButton boardId={board.id} boardName={board.name} />
      </div>
    </div>
  );
};
// ---- /DnD row component ----

const buttonStyle = (bgColor, textColor = '#fff') => ({
  padding: '0.3rem 0.75rem',
  backgroundColor: bgColor,
  color: textColor,
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer'
});

const overlayStyles = {
  container: {
    position: 'absolute',
    inset: 0,
    background: 'rgba(255,255,255,0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none',
    borderRadius: 6
  },
  card: {
    background: '#fff',
    padding: 16,
    borderRadius: 12,
    boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
    textAlign: 'center'
  }
};

// Lightweight inline modal styles for the first-login tutorial prompt
const promptStyles = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.35)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999
  },
  modal: {
    width: 'min(90vw, 420px)',
    background: '#fff',
    borderRadius: 12,
    boxShadow: '0 12px 28px rgba(0,0,0,0.25)',
    padding: '16px 16px 12px'
  }
};

export default BoardList;
